/**
 * pagination.ts — query/response shapes for list endpoints.
 *
 * API-101 AC: "pagination for list endpoints". Cursor-based for hot tables
 * (daily_tracking, audit_trail) where offset pagination would be expensive
 * at scale; offset-based is enough for the rest.
 */

import { z } from 'zod';

/** Offset-based — fine for everything except hot tables. */
export const OffsetPaginationQuery = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .describe('Page-based pagination query.');
export type OffsetPaginationQuery = z.infer<typeof OffsetPaginationQuery>;

export const OffsetPaginationMeta = z
  .object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(100),
    totalItems: z.number().int().min(0),
    totalPages: z.number().int().min(0),
  })
  .describe('Page-based pagination metadata.');
export type OffsetPaginationMeta = z.infer<typeof OffsetPaginationMeta>;

/** Cursor-based — for hot tables (daily_tracking, audit_trail). */
export const CursorPaginationQuery = z
  .object({
    cursor: z.string().optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .describe('Cursor-based pagination query.');
export type CursorPaginationQuery = z.infer<typeof CursorPaginationQuery>;

export const CursorPaginationMeta = z
  .object({
    nextCursor: z.string().nullable(),
    pageSize: z.number().int().min(1).max(100),
  })
  .describe('Cursor-based pagination metadata.');
export type CursorPaginationMeta = z.infer<typeof CursorPaginationMeta>;
